import merge from 'lodash/merge';
import includes from 'lodash/includes';
import ReducerBuilder from 'classes/ReducerBuilder';
import userManagementInitialState from './initialState';
import {Types as ViewOptionsActionTypes} from 'viewOptions/actions';
import {Types as ResourceActionTypes} from 'resources/actions';
import {Types as UserActionTypes} from './actions';
import {SectionedColumns} from './utils';
import {FieldSavedResourceActionFor} from "../../resources/actions";
import * as ResourceApi from "../../api/resources";

const handleSearchInProgress = (state, action) => {
  if (action.resourceType === state.filterResource) {
    state.searchingUsers = true;
  }
};

const persistentColumns = [
  'Select',
  'Id',
  'FullName',
  'Email'
];

export default ReducerBuilder.make(userManagementInitialState)

  // create user

  .forType(UserActionTypes.START_CREATE_USER)
  .reduce((state, action) => {
    state.newUserId = null;
    state.creatingUser = true;
  })
  .forType(UserActionTypes.SUBMIT_CREATE_USER)
  .reduce((state, action) => {
    state.createUserSubmitted = true;
  })
  .forType(UserActionTypes.END_CREATE_USER)
  .reduce((state, action) => {
    state.creatingUser = false;
    state.createUserSubmitted = false;
  })
  .forType(UserActionTypes.CREATE_USER_SUCCESS)
  .reduce((state, action) => {
    state.newUserId = action.uid;
  })

  // user search

  .forType(UserActionTypes.REGISTER_FIELDS)
  .reduce((state, action) => {
    state.currentQuery = action.formFields.reduce((emptyQuery, {name}) => {
      emptyQuery[name] = null;
      return emptyQuery;
    }, {});
  })

  .forType(UserActionTypes.SET_QUERY)
  .reduce((state, {query}) => {
    state.currentQuery = query;
  })

  .forType(UserActionTypes.CLEAR_QUERY)
  .reduce((state, action) => {
    state.currentQuery = userManagementInitialState.currentQuery;
  })

  .forType(UserActionTypes.UPDATE_QUERY)
  .reduce((state, {field, value}) => {
    state.currentQuery[field] = value;
  })

  .forType(UserActionTypes.SET_FILTER_RESOURCE)
  .reduce((state, action) => {
    state.filterResource = action.filterResource;
  })

  .forType(UserActionTypes.SET_INITIAL_COLUMNS)
  .reduce((state, action) => {
    state.tableColumns = action.columns.map(({name}) => ({
      name,
      visible: true,
      disabled: includes(persistentColumns, name)
    }));
  })
  .forType(UserActionTypes.SET_VISIBLE_COLUMNS_MODAL)
  .reduce((state, action) => {
    state.isVisibleColumnsModalOpen = action.isOpen;
    state.tableColumnsInProgress = action.isOpen ? state.tableColumns.slice() : [];
  })
  .forType(UserActionTypes.UPDATE_VISIBLE_COLUMNS)
  .reduce((state, action) => {
    const sectionFields = SectionedColumns[action.sectionName];

    state.tableColumnsInProgress = state.tableColumnsInProgress.map(col => {
      if (includes(sectionFields, col.name)) {
        const isPersistent = includes(persistentColumns, col.name);
        const isNowVisible = includes(action.visibleColumns, col.name);

        return {...col, visible: isPersistent || isNowVisible};
      } else {
        return col;
      }
    });
  })
  .forType(UserActionTypes.SAVE_VISIBLE_COLUMNS)
  .reduce((state, action) => {
    state.tableColumns = state.tableColumnsInProgress.slice();
  })

  .forType(UserActionTypes.UPDATE_USER_SELECTION)
  .reduce((state, action) => {
    if (action.selected === true) {
      state.selectedUsers[action.id] = null;
    } else if (action.selected === false) {
      delete state.selectedUsers[action.id];
    }
  })


  .forType(ResourceActionTypes.GET_RESOURCE_PAGE)
  .reduce(handleSearchInProgress)

  .forType(ViewOptionsActionTypes.TOGGLE_SORT_FOR_RESOURCE)
  .reduce(handleSearchInProgress)

  .forType(ResourceActionTypes.RECEIVED_RESOURCE_PAGE)
  .reduce((state, action) => {
    if (action.resourceType === state.filterResource) {
      state.searchingUsers = false;

      if (state.pristine) {
        state.pristine = false;
      }
    }
  })

  // the action that kicks off all API requests for relevant user data.
  .forType(UserActionTypes.REQUEST_USER_DETAILS)
  .reduce((state, {userId}) => {
    state.currentUser  = null; 
    state.newUserId = null;
    state.fetchingUser = true;
    state.currentUserId = userId;

    state.loadingUserProfile = true;
    state.loadingStartsCompletes = true;
    state.loadingRaffleEntries = true;
    state.loadingPrizeboothOrders = true;
    state.loadingPointHistory = true;
  })
  .forType(FieldSavedResourceActionFor(ResourceApi.Types.User))
  .reduce((state, {id, saveData}) => {
    if (id.toString() === state.currentUserId) {
      // update user here with one received
      state.currentUser = saveData;
    }
  })

  // user profile

  .forType(UserActionTypes.RECEIVED_USER_RESOURCE)
  .reduce((state, action) => {
    state.loadingUserProfile = false;
    state.currentUser = merge({}, state.currentUser, action.user);
  })
  .forType(UserActionTypes.FAILED_USER_RESOURCE)
  .reduce((state, action) => {
    state.loadingUserProfile = false;
    state.failedUserProfile = action.ajaxError;
  })

  // starts and completes

  .forType(UserActionTypes.RECEIVED_STARTS_COMPLETES)
  .reduce((state, action) => {
    state.loadingStartsCompletes = false;
    state.currentUser = merge({}, state.currentUser, {startsCompletes: action.startsCompletes});
  })
  .forType(UserActionTypes.FAILED_STARTS_COMPLETES)
  .reduce((state, action) => {
    state.loadingStartsCompletes = false;
    state.failedStartsCompletes = action.ajaxError;
  })

  // sweeps entries

  .forType(UserActionTypes.RECEIVED_RAFFLE_ENTRIES)
  .reduce((state, action) => {
    state.loadingRaffleEntries = false;
    state.currentUser = merge({}, state.currentUser, {raffleEntries: action.raffleEntries});
  })
  .forType(UserActionTypes.FAILED_RAFFLE_ENTRIES)
  .reduce((state, action) => {
    state.loadingRaffleEntries = false;
    state.failedRaffleEntries = action.ajaxError;
  })

  // store purchases

  .forType(UserActionTypes.RECEIVED_PRIZEBOOTH_ORDERS)
  .reduce((state, action) => {
    state.loadingPrizeboothOrders = false;
    state.currentUser = merge({}, state.currentUser, {prizeboothOrders: action.prizeboothOrders});
  })
  .forType(UserActionTypes.FAILED_PRIZEBOOTH_ORDERS)
  .reduce((state, action) => {
    state.loadingPrizeboothOrders = false;
    state.failedPrizeboothOrders = action.ajaxError;
  })

  // point history

  .forType(UserActionTypes.RECEIVED_POINT_HISTORY)
  .reduce((state, action) => {
    state.loadingPointHistory = false;
    state.currentUser = merge({}, state.currentUser, {pointHistory: action.pointHistory});
  })
  .forType(UserActionTypes.FAILED_POINT_HISTORY)
  .reduce((state, action) => {
    state.loadingPointHistory = false;
    state.failedPointHistory = action.ajaxError;
  })


  // award points

  .forType(UserActionTypes.START_AWARD_POINTS)
  .reduce((state, action) => {
    state.awardingPoints = true;
  })
  .forType(UserActionTypes.SUBMIT_AWARD_POINTS)
  .reduce((state, action) => {
    state.awardPointsSubmitted = true;
  })
  .forType(UserActionTypes.END_AWARD_POINTS)
  .reduce((state, action) => {
    state.awardingPoints = false;
    state.awardPointsSubmitted = false;
  })
  .forType(UserActionTypes.ERROR_AWARDING_POINTS)
  .reduce((state, action) => {
    state.awardPointsSubmitted = false;
    state.awardPointsError = action.message;
  })

  // award spins

  .forType(UserActionTypes.START_AWARD_SPINS)
  .reduce((state, action) => {
    state.awardingSpins = true;
  })
  .forType(UserActionTypes.SUBMIT_AWARD_SPINS)
  .reduce((state, action) => {
    state.awardSpinsSubmitted = true;
  })
  .forType(UserActionTypes.END_AWARD_SPINS)
  .reduce((state, action) => {
    state.awardingSpins = false;
    state.awardSpinsSubmitted = false;
  })

  // award consecutive days

  .forType(UserActionTypes.START_AWARD_DAYS)
  .reduce((state, action) => {
    state.awardingDays = true;
  })
  .forType(UserActionTypes.SUBMIT_AWARD_DAYS)
  .reduce((state, action) => {
    state.awardDaysSubmitted = true;
  })
  .forType(UserActionTypes.END_AWARD_DAYS)
  .reduce((state, action) => {
    state.awardingDays = false;
    state.awardDaysSubmitted = false;
  })

  // change email

  .forType(UserActionTypes.START_CHANGE_EMAIL)
  .reduce((state, action) => {
    state.changingEmail = true;
  })
  .forType(UserActionTypes.SUBMIT_CHANGE_EMAIL)
  .reduce((state, action) => {
    state.changeEmailSubmitted = true;
  })
  .forType(UserActionTypes.END_CHANGE_EMAIL)
  .reduce((state, action) => {
    state.changingEmail = false;
    state.changeEmailSubmitted = false;
  })
  
  // change password

  .forType(UserActionTypes.START_CHANGE_PASSWORD)
  .reduce((state, action) => {
    state.changingPassword = true;
  })
  .forType(UserActionTypes.SUBMIT_CHANGE_PASSWORD)
  .reduce((state, action) => {
    state.changePasswordSubmitted = true;
  })
  .forType(UserActionTypes.END_CHANGE_PASSWORD)
  .reduce((state, action) => {
    state.changingPassword = false;
    state.changePasswordSubmitted = false;
  })

  // repair profile

  .forType(UserActionTypes.START_REPAIR_PROFILE)
  .reduce((state, action) => {
    state.repairingProfile = true;
  })
  .forType(UserActionTypes.SUBMIT_REPAIR_PROFILE)
  .reduce((state, action) => {
    state.repairProfileSubmitted = true;
  })
  .forType(UserActionTypes.END_REPAIR_PROFILE)
  .reduce((state, action) => {
    state.repairingProfile = false;
    state.repairProfileSubmitted = false;
  })

  // revoke points by amount

  .forType(UserActionTypes.START_REVOKE_AMOUNT_OF_POINTS)
  .reduce((state, action) => {
    state.revokingAmountOfPoints = true;
  })
  .forType(UserActionTypes.SUBMIT_REVOKE_AMOUNT_OF_POINTS)
  .reduce((state, action) => {
    state.revokingAmountOfPointsSubmitted = true;
  })
  .forType(UserActionTypes.END_REVOKE_AMOUNT_OF_POINTS)
  .reduce((state, action) => {
    state.revokingAmountOfPoints = false;
    state.revokingAmountOfPointsSubmitted = false;
  })

  // cancel discrete transactions in user point history

  .forType(UserActionTypes.START_REVOKE_POINTS)
  .reduce((state, {phid, amount}) => {
    state.revokingPoints = true;
    state.currentRevocation = {phid, amount};
  })
  .forType(UserActionTypes.SUBMIT_REVOKE_POINTS)
  .reduce((state, action) => {
    state.revokePointsSubmitted = true;
  })
  .forType(UserActionTypes.END_REVOKE_POINTS)
  .reduce((state, action) => {
    state.revokingPoints = false;
    state.currentRevocation = null;
    state.revokePointsSubmitted = false;
  })

  // delete user data

  .forType(UserActionTypes.START_DELETE_USER_DATA)
  .reduce((state, action) => {
    console.log('start delete', action);
    state.confirmUserDataDeletion = true;
    state.userToDeleteData = action.user;
  })
  .forType(UserActionTypes.END_DELETE_USER_DATA)
  .reduce((state, action) => {
    state.confirmUserDataDeletion = false;
    state.userToDeleteData = null;
  })
  .build();



// WEBPACK FOOTER //
// ./public/pages/UserManagement/reducer.js